import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { ProgressionQuestion } from 'models/WorkflowConfiguration';
import { Form } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import { getHasElementsInArray } from 'utils';

import './DataRoomInterestQuestionModal.styles.scss';

type DataRoomInterestQuestionModalProps = {
  className?: string;
  pendingProgressionQuestion: ProgressionQuestion;
  handleSubmit: (answer: string) => Promise<void>;
  onBack?: () => void;
} & ModalProps;

const DataRoomInterestQuestionModal: React.ForwardRefRenderFunction<
  ModalRef,
  DataRoomInterestQuestionModalProps
> = (props, ref) => {
  const {
    className,
    pendingProgressionQuestion,
    handleSubmit,
    onBack,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('data-room-interest-question-modal', className);

  const { t } = useTranslation();

  const answerOptions = useMemo(
    () =>
      getHasElementsInArray(pendingProgressionQuestion?.answers)
        ? pendingProgressionQuestion.answers.map(({ _id, text }) => ({
            value: _id,
            label: text,
          }))
        : [],
    [pendingProgressionQuestion?.answers],
  );

  const onSubmit = useCallback(
    async ({ answer }) => await handleSubmit(answer),
    [handleSubmit],
  );

  return (
    <Modal
      ref={modalRef}
      title={t('quickQuestion')}
      className={classes}
      {...rest}
    >
      <div className="data-room-interest-question-modal__content">
        <p>{pendingProgressionQuestion?.question}</p>

        <Form
          onSubmit={onSubmit}
          render={({ submitting, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="data-room-interest-question-modal__options">
                <RadioGroupField
                  name="answer"
                  className="radio"
                  options={answerOptions}
                />
              </div>

              <div className="actions">
                {onBack && (
                  <Button type="button" variant="outline" onClick={onBack}>
                    {t('back')}
                  </Button>
                )}
                <Button type="submit" disabled={submitting}>
                  {t('submit')}
                </Button>
              </div>
            </form>
          )}
        />
      </div>
    </Modal>
  );
};

export default forwardRef(DataRoomInterestQuestionModal);
